import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    ColorSwatch,
    ColorSlider
} from "ncore-ui-kit";
import {
    BulletList,
    DocsTable,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    oklchToHex
} from "../../create/color";

const NAME = "ColorSlider";

const USAGE_CODE = `const [hue, setHue] = useState(180);

<ColorSlider
    color={oklchToHex(0.62, 0.15, hue)}
    onChange={setHue}
    channel="hue"
    value={hue}
/>`;

const CHANNELS_CODE = `<ColorSlider
    onChange={setLightness}
    channel="lightness"
    value={lightness}
    color={color}
/>

<ColorSlider
    onChange={setChroma}
    channel="chroma"
    value={chroma}
    color={color}
/>

<ColorSlider
    onChange={setAlpha}
    channel="alpha"
    value={alpha}
    color={color}
/>`;

const HUE_START = 180;

const LIGHTNESS_START = 0.62;

const CHROMA_START = 0.15;

const ALPHA_START = 1;

const ALPHA_SCALE = 255;

const HEX_RADIX = 16;

const FIXED_COLOR = "#2f8f83";

const SLIDER_LENGTH = {
    width: 280
};

const VERTICAL_LENGTH = {
    height: 160
};

const getHexWithAlpha = (hex: string, alpha: number) => `${hex}${Math.round(alpha * ALPHA_SCALE).toString(HEX_RADIX).padStart(2, "0")}`;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("colorSliderPage.channelsTitle"),
        id: "channels"
    },
    {
        title: t("colorSliderPage.orientationTitle"),
        id: "orientation"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const OverviewPreview = () => {
    const {
        t
    } = useDocsI18n();

    const [
        hue,
        setHue
    ] = useState<number>(HUE_START);

    const hueColor = oklchToHex(LIGHTNESS_START, CHROMA_START, hue);

    return <Preview
        caption={t("colorSliderPage.overviewCaption", [
            hue
        ])}
        isStacked={true}
    >
        <ColorSlider
            style={SLIDER_LENGTH}
            onChange={setHue}
            color={hueColor}
            channel="hue"
            value={hue}
        />
        <ColorSwatch
            color={hueColor}
            size="large"
        />
    </Preview>;
};

const ChannelsPreview = () => {
    const [
        lightness,
        setLightness
    ] = useState<number>(LIGHTNESS_START);

    const [
        chroma,
        setChroma
    ] = useState<number>(CHROMA_START);

    const [
        alpha,
        setAlpha
    ] = useState<number>(ALPHA_START);

    const baseColor = oklchToHex(lightness, chroma, HUE_START);

    const fullColor = getHexWithAlpha(baseColor, alpha);

    return <Preview
        isStacked={true}
    >
        <ColorSlider
            onChange={setLightness}
            style={SLIDER_LENGTH}
            channel="lightness"
            color={fullColor}
            value={lightness}
        />
        <ColorSlider
            style={SLIDER_LENGTH}
            onChange={setChroma}
            color={fullColor}
            channel="chroma"
            value={chroma}
        />
        <ColorSlider
            style={SLIDER_LENGTH}
            onChange={setAlpha}
            color={fullColor}
            channel="alpha"
            value={alpha}
        />
        <ColorSwatch
            color={fullColor}
            size="large"
        />
    </Preview>;
};

const ColorSliderPage = () => {
    const {
        t
    } = useDocsI18n();

    return <Article>
        <ComponentHeader
            lead={t("colorSliderPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("colorSliderPage.overviewText")}
            </Paragraph>
            <OverviewPreview />
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("colorSliderPage.channelsTitle")}
            id="channels"
        >
            <Paragraph>
                {t("colorSliderPage.channelsText")}
            </Paragraph>
            <Paragraph>
                {t("colorSliderPage.hueText")}
            </Paragraph>
            <Paragraph>
                {t("colorSliderPage.chromaText")}
            </Paragraph>
            <ChannelsPreview />
            <CodeBlock
                code={CHANNELS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("colorSliderPage.orientationTitle")}
            id="orientation"
        >
            <Paragraph>
                {t("colorSliderPage.orientationText")}
            </Paragraph>
            <Preview>
                <ColorSlider
                    title={t("colorSliderPage.verticalHue")}
                    style={VERTICAL_LENGTH}
                    orientation="vertical"
                    color={FIXED_COLOR}
                    channel="hue"
                />
                <ColorSlider
                    title={t("colorSliderPage.verticalAlpha")}
                    style={VERTICAL_LENGTH}
                    orientation="vertical"
                    color={FIXED_COLOR}
                    channel="alpha"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("colorSliderPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <ColorSlider
                    style={SLIDER_LENGTH}
                    color={FIXED_COLOR}
                    channel="hue"
                />
                <ColorSlider
                    style={SLIDER_LENGTH}
                    color={FIXED_COLOR}
                    isDisabled={true}
                    channel="hue"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("colorSliderPage.a11yRole"),
                    t("colorSliderPage.a11yName"),
                    t("colorSliderPage.a11yNative"),
                    t("colorSliderPage.a11yDisabled"),
                    t("colorSliderPage.a11yCommit")
                ]}
            />
            <DocsTable
                rows={[
                    [
                        t("colorSliderPage.keyArrows"),
                        t("colorSliderPage.actionArrows")
                    ],
                    [
                        t("colorSliderPage.keyShift"),
                        t("colorSliderPage.actionShift")
                    ],
                    [
                        t("colorSliderPage.keyPage"),
                        t("colorSliderPage.actionPage")
                    ],
                    [
                        t("colorSliderPage.keyHomeEnd"),
                        t("colorSliderPage.actionHomeEnd")
                    ]
                ]}
                headers={[
                    t("colorSliderPage.keyHeader"),
                    t("colorSliderPage.actionHeader")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "\"hue\" | \"alpha\" | \"lightness\" | \"chroma\"",
                        description: t("colorSliderPage.propChannel"),
                        name: "channel"
                    },
                    {
                        description: t("colorSliderPage.propColor"),
                        type: "string",
                        name: "color"
                    },
                    {
                        description: t("colorSliderPage.propValue"),
                        type: "number",
                        name: "value"
                    },
                    {
                        description: t("colorSliderPage.propInitialValue"),
                        name: "initialValue",
                        type: "number"
                    },
                    {
                        description: t("colorSliderPage.propOnChange"),
                        type: "(value: number) => void",
                        name: "onChange"
                    },
                    {
                        description: t("colorSliderPage.propOnChangeEnd"),
                        type: "(value: number) => void",
                        name: "onChangeEnd"
                    },
                    {
                        description: t("colorSliderPage.propMax"),
                        defaultValue: "0.37",
                        type: "number",
                        name: "max"
                    },
                    {
                        description: t("colorSliderPage.propIsChromaRelative"),
                        name: "isChromaRelative",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("colorSliderPage.propOrientation"),
                        type: "\"horizontal\" | \"vertical\"",
                        defaultValue: "\"horizontal\"",
                        name: "orientation"
                    },
                    {
                        description: t("colorSliderPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("colorSliderPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        description: t("colorSliderPage.propIsShowValue"),
                        defaultValue: "Boolean(title)",
                        name: "isShowValue",
                        type: "boolean"
                    },
                    {
                        description: t("colorSliderPage.propFormatValueText"),
                        type: "(value: number) => string",
                        name: "formatValueText"
                    },
                    {
                        description: t("colorSliderPage.propSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("colorSliderPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("colorSliderPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("colorSliderPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ColorSliderPage;
